"use client";

import React from "react";
import { useTranslations } from "next-intl";
import {
  MdOutlineLocationOn,
  MdCheckCircle,
  MdVisibilityOff,
  MdErrorOutline,
} from "react-icons/md";
import styles from "./location.module.css";
import { useListingContext } from "../listingContex";
import MapLibreSelector from "./mapLibreSelector";

export default function LocationSection() {
  const t = useTranslations("NewListing");
  const { data, updateField, validationErrors } = useListingContext();

  if (!data) return <LocationSkeleton />;

  const hasLocation = !!data.map_location;
  const locationOnRequest = data.location_on_request || false;

  let displayAddress = "";

  if (hasLocation) {
    // Grab the English macro parts for the draft display
    const city = data.city?.en || "";
    const region = data.region?.en || "";
    const country = data.country?.en || "";

    if (locationOnRequest) {
      const parts = [city, region, country].filter(Boolean);
      displayAddress =
        parts.length > 0 ? parts.join(", ") : "Approximate location saved.";
    } else {
      const parts = [data.native_street_line, city, region, country].filter(
        Boolean,
      );

      displayAddress =
        parts.length > 0
          ? parts.join(", ")
          : data.address?.en || "Fetching address details...";
    }
  }

  return (
    <section className={styles.transparentContainer} id="map_location">
      <div className={styles.titleGroup}>
        <h2 className={styles.sectionTitle}>Location</h2>
        {validationErrors?.map_location && (
          <span className={styles.inlineError}>
            <MdErrorOutline /> {t(`errors.${validationErrors.map_location}`)}
          </span>
        )}
      </div>
      <p className={styles.sectionSubtitle}>
        Pinpoint the exact location on the map. We will automatically fetch the
        street address, city, and region for you.
      </p>

      {/* The Map Component */}
      <MapLibreSelector />

      {/* Resolved Address Display Card */}
      <div
        className={`${styles.addressCard} ${hasLocation ? styles.addressCardActive : ""} ${validationErrors?.map_location ? styles.errorCard : ""}`}
      >
        {/* Left Side: Icon & Address */}
        <div className={styles.addressLeft}>
          <div className={styles.addressIconWrapper}>
            {locationOnRequest ? (
              <MdVisibilityOff size={24} />
            ) : (
              <MdOutlineLocationOn size={24} />
            )}
          </div>

          <div className={styles.addressDetails}>
            <h4 className={styles.addressTitle}>
              {hasLocation
                ? locationOnRequest
                  ? "Approximate Location"
                  : "Exact Location"
                : "No Location Selected"}
            </h4>
            <p className={styles.addressText}>
              {hasLocation
                ? displayAddress || "Fetching address details..."
                : "Click anywhere on the map to place a pin."}
            </p>
          </div>
        </div>

        {/* Right Side: Toggles & Status */}
        <div className={styles.addressRight}>
          {hasLocation && (
            <div className={styles.toggleWrapper}>
              <span className={styles.toggleLabel}>Hide exact address</span>
              <button
                type="button"
                className={`${styles.toggleSwitch} ${locationOnRequest ? styles.toggleActive : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  updateField("location_on_request", !locationOnRequest);
                }}
              >
                <div className={styles.toggleThumb}></div>
              </button>
            </div>
          )}

          {hasLocation && data.address?.en && !locationOnRequest && (
            <div className={styles.successBadge}>
              <MdCheckCircle size={24} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function LocationSkeleton() {
  return (
    <section className={styles.transparentContainer}>
      {/* Title & Subtitle */}
      <div className={`${styles.skeletonPulse} ${styles.skelTitle}`} />
      <div className={`${styles.skeletonPulse} ${styles.skelSubtitle}`} />
      <div
        className={`${styles.skeletonPulse} ${styles.skelSubtitle}`}
        style={{ width: "60%", marginTop: "4px" }}
      />

      {/* Map Placeholder */}
      <div className={`${styles.skeletonPulse} ${styles.skelMap}`} />

      {/* Address Card Placeholder */}
      <div className={styles.addressCard}>
        <div className={styles.addressLeft}>
          <div
            className={`${styles.skeletonPulse} ${styles.skelAddressIcon}`}
          />
          <div className={styles.addressDetails} style={{ width: "100%" }}>
            <div
              className={`${styles.skeletonPulse} ${styles.skelAddressTitle}`}
            />
            <div
              className={`${styles.skeletonPulse} ${styles.skelAddressText}`}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
